import React from 'react';
import { ChevronDown } from 'lucide-react';
import type { FlowNodeProps } from './types';
import { MAX_DEPTH, MAX_NODES_PER_LEVEL } from './types';

export const FlowNode = React.memo(({
  question,
  level,
  hierarchicalNumber,
  getFollowUpQuestions,
  questionCTAs,
  expandedNodes,
  onToggle,
  visitedPath = new Set<string>(),
  onEditQuestion
}: FlowNodeProps) => {
  if (level >= MAX_DEPTH) {
    return (
      <div className={`${level > 0 ? 'ml-4' : ''}`}>
        <div className="bg-red-50 border border-red-200  p-3 mb-2">
          <span className="text-sm text-red-700">⚠️ Max depth reached ({MAX_DEPTH})</span>
        </div>
      </div>
    );
  }

  if (visitedPath.has(question.id)) {
    return (
      <div className={`${level > 0 ? 'ml-4' : ''} mb-2`}>
        <div className={`bg-gray-50 p-4 flex items-center justify-between ${level > 0 ? 'border-l-4 border-l-gray-300' : ''}`}>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border border-gray-300 bg-white">
              <span className="text-xs font-semibold text-gray-500">{hierarchicalNumber}</span>
            </div>
            <span className="text-sm font-medium text-gray-700">{question.question}</span>
          </div>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            Back to root question ↩
          </span>
        </div>
      </div>
    );
  }

  const followUps = getFollowUpQuestions(question.id);
  const hasFollowUps = followUps.length > 0;
  const isExpanded = expandedNodes.has(question.id);
  const assignedCTAs = questionCTAs[question.id] || [];
  const newVisitedPath = new Set([...visitedPath, question.id]);

  const borderClass = level === 1
    ? 'border-l-4 border-l-indigo-500'
    : level >= 2
      ? 'border-l-4 border-l-purple-500'
      : '';

  const indentClass = level === 0 ? '' : level === 1 ? 'ml-4' : level === 2 ? 'ml-8' : 'ml-12';

  const levelBadge = level === 0
    ? (question.is_root ? <span className="px-2 py-0.5  text-xs font-medium bg-yellow-100 text-yellow-800">Root</span> : null)
    : <span className={`px-2 py-0.5  text-xs font-medium ${level === 1 ? 'bg-indigo-100 text-indigo-800' : 'bg-purple-100 text-purple-800'}`}>L{level}</span>;

  return (
    <div className={`${indentClass} mb-2`}>
      <div
        className={`bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition-colors ${borderClass} ${!question.is_active ? 'opacity-60' : ''}`}
        onClick={() => onEditQuestion(question)}
      >
        {level > 0 && (
          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
            Follow up question
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border bg-white ${level === 0 ? 'border-indigo-200' : level === 1 ? 'border-indigo-200' : 'border-purple-200'}`}>
              <span className={`text-xs font-semibold ${level === 0 ? 'text-indigo-600' : level === 1 ? 'text-indigo-600' : 'text-purple-600'}`}>
                {hierarchicalNumber}
              </span>
            </div>

            <span className="text-sm font-semibold text-gray-900 truncate">{question.question}</span>

            {hasFollowUps && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(question.id);
                }}
                className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100  hover:bg-gray-200 flex items-center gap-1"
              >
                <span>Display follow-up</span>
                <ChevronDown className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {levelBadge}
            <span className={`px-2 py-0.5  text-xs font-medium ${question.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
              {question.is_active ? 'Active' : 'Inactive'}
            </span>
            {hasFollowUps && (
              <span className="px-2 py-0.5  text-xs font-medium bg-green-100 text-green-700">
                {followUps.length} follow-up question{followUps.length !== 1 ? 's' : ''}
              </span>
            )}
            {assignedCTAs.length > 0 ? (
              <span className="px-2 py-0.5  text-xs font-medium bg-emerald-100 text-emerald-700">
                {assignedCTAs.length} CTA{assignedCTAs.length !== 1 ? 's' : ''}
              </span>
            ) : (
              <span className="px-2 py-0.5  text-xs font-medium bg-red-100 text-red-700">
                No CTA
              </span>
            )}
            {question.asset_tags && question.asset_tags.length > 0 && (
              <span className="px-2 py-0.5  text-xs font-medium bg-gray-100 text-gray-600">
                {question.asset_tags[0]}
              </span>
            )}
          </div>
        </div>
      </div>

      {isExpanded && hasFollowUps && (
        <div className="mt-1">
          {followUps.slice(0, MAX_NODES_PER_LEVEL).map((followUp, idx) => (
            <FlowNode
              key={followUp.id}
              question={followUp}
              level={level + 1}
              hierarchicalNumber={`${hierarchicalNumber}.${idx + 1}`}
              getFollowUpQuestions={getFollowUpQuestions}
              questionCTAs={questionCTAs}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
              visitedPath={newVisitedPath}
              onEditQuestion={onEditQuestion}
            />
          ))}
          {followUps.length > MAX_NODES_PER_LEVEL && (
            <div className={`${level >= 2 ? 'ml-12' : level === 1 ? 'ml-8' : 'ml-4'} mb-2`}>
              <div className="bg-gray-50  p-3 text-sm text-gray-500">
                +{followUps.length - MAX_NODES_PER_LEVEL} more questions not shown
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

FlowNode.displayName = 'FlowNode';
